/**
 * KunyeBasligi.jsx — Kişi Detay sayfasının üstündeki künye kartı (backlog #367; backend #366 künye, #351 fotoğraf).
 *
 * Yalnızca KİŞİNİN KENDİ verisi: vesikalık, ad soyad, tam T.C. / pasaport no, #id, uyruk, telefon, e-posta
 * (+ varsa pasaport no ve ülkesi). Sekmelerden ulaşılan hiçbir şey burada durmaz: işçi/işveren, Özel İzin, kart durumu,
 * Firma İSG Belgeleri, Yasaklı, Sigortalı rozetleri ve "Görünüm: firma · fuar" satırı kalktı (#367 REVERSES #248/#264/#292).
 *
 * Tek istisna (#479, #367'yi kısmen geri alır): üst çubuktaki fuarda kişinin eşli olduğu firma. Kart Yazdır'ın şeridiyle aynı
 * seçim (`kartKararBirlestir`: kartın basılacağı firma) + birden çok firma varsa "+N" ve ipucunda öteki firmaların adları.
 * Fuar adı / seçici EKLENMEZ: üst çubuk fuarı referanstır, başka görünüm için üst çubuktaki fuar değiştirilir. Fuar seçili değilse
 * ya da kişinin o fuarda görevlendirmesi yoksa hiçbir şey yazılmaz.
 *
 * Sağ üstteki "Düzenle ve Gör" düğmesi KunyeDuzenleModal'ı açar (künye PATCH'i + Ek Bilgiler). Vesikalığı değiştiren
 * kamera düğmesi `POST /personnel/{id}/fotograf` ile yükler (staff her zaman değiştirebilir). Her iki yazmadan sonra
 * `onChanged(mesaj)`: sayfa kişiyi yeniden okur; hata `onError(mesaj)`.
 *
 * Veri: ad / T.C. / telefon / e-posta önce `GET /personnel/{id}/kunye`dan (yazmadan hemen sonra tazedir), okunamazsa kişi
 * satırından (`person`, Kişi Listesi satırı). Uyruk ve pasaport yalnızca künyede olduğu için o okuma bitene dek "…".
 * Vesikalık yalnızca `person.fotografVar` iken çekilir (eski dosyaların çoğu 404; bayrak istek atmamayı sağlar).
 *
 * @param {number}   personId
 * @param {object}   person       Kişi Listesi satırı (`detay.kisi`)
 * @param {number}   fuarId       üst çubuk fuarı (Ek Bilgiler'deki fuar-bazlı kategoriler için; yoksa null)
 * @param {object[]} firmalar     `detay.firmalar[]` — kişinin o fuardaki firma başına kart kararları (yoksa/boşsa firma satırı çıkmaz)
 * @param {number}   tercihFirmaId `detay.kartFirmaId` — eşitlikte öne alınacak firma
 * @param {number}   reloadToken  sayfa yenilenince künye yeniden okunur
 * @param {boolean}  salt         salt okunur: "Düzenle ve Gör" ve kamera gizlenir
 * @param {Function} onChanged    `(mesaj)` — yazma başarılı
 * @param {Function} onError      `(mesaj)` — fotoğraf yüklenemedi
 */
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { BookOpen, Building2, Camera, Globe, IdCard, Mail, Pencil, Phone, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchKisiFotograf } from "../../../services/personelRosterService";
import { getKunye, getUlkeListesi, uploadKisiFotograf } from "../../../services/kisiKunyeService";
import KunyeDuzenleModal from "./KunyeDuzenleModal";
import { PanelDavetiDugmesi } from "../PanelDavetiModal";
import { kartKararBirlestir } from "../kartBirlestir";
import { firmaAdi } from "../PersonelFirmaBelgeParcalari";

// WAS (until #479): const UYRUK = { 1: "T.C.", 2: "Yabancı" }; — the fallback names are tr / en keys now.
const UYRUK_ANAHTAR = { 1: "uyrukTc", 2: "uyrukYabanci" };
const FOTO_TURLERI = /^image\/(jpeg|png|webp)$/i;
const FOTO_UZANTILARI = /\.(jpe?g|png|webp)$/i;

function Bilgi({ Icon, etiket, children }) {
  return (
    <div>
      <div className="text-muted small d-flex align-items-center gap-1"><Icon size={12} />{etiket}</div>
      <div className="fw-medium text-break">{children || <span className="text-muted fw-normal">—</span>}</div>
    </div>
  );
}

export default function KunyeBasligi({
  personId, person, fuarId, firmalar = null, tercihFirmaId = null, reloadToken = 0, salt = false, onChanged, onError,
}) {
  const { t } = useTranslation("components");
  const girdiRef = useRef(null);
  const [kunye, setKunye] = useState(undefined); // undefined: okunuyor · null: okunamadı
  const [kunyeHata, setKunyeHata] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [duzenleAcik, setDuzenleAcik] = useState(false);
  const [ulkeAdlari, setUlkeAdlari] = useState(null);
  const [foto, setFoto] = useState(null); // { id, url }
  const [fotoSayac, setFotoSayac] = useState(0);
  const [fotoYuklenenId, setFotoYuklenenId] = useState(null); // bu oturumda yüklenen kişi (person.fotografVar henüz tazelenmemiş olabilir)
  const [yukleniyor, setYukleniyor] = useState(false);

  // ── Künye ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!personId) return undefined;
    let iptal = false;
    async function oku() {
      setKunyeHata(null);
      try {
        const k = await getKunye(personId);
        if (!iptal) setKunye(k);
      } catch (e) {
        if (!iptal) {
          setKunye(null);
          setKunyeHata(e.message);
        }
      }
    }
    oku();
    return () => { iptal = true; };
  }, [personId, reloadToken, yenile]);

  // Başka kişiye geçilirken önceki kişinin künyesi gösterilmesin.
  const k = kunye && Number(kunye.personId) === Number(personId) ? kunye : null;
  const yukleniyorKunye = kunye === undefined || (kunye && !k);

  // ── Pasaport ülkesinin adı (yalnızca ülke kimliği varken liste çekilir) ───────────────
  const ulkeId = k?.pasaportUlkeId ?? null;
  useEffect(() => {
    if (ulkeId == null) return undefined;
    let iptal = false;
    async function oku() {
      try {
        const liste = await getUlkeListesi();
        if (!iptal) setUlkeAdlari(Object.fromEntries(liste.map((u) => [String(u.countryId), u.countryName])));
      } catch {
        /* ad okunamazsa yalnızca pasaport numarası görünür */
      }
    }
    oku();
    return () => { iptal = true; };
  }, [ulkeId]);

  // ── Vesikalık ──────────────────────────────────────────────────────────────────
  const fotoVar = Boolean(person?.fotografVar) || (fotoYuklenenId != null && fotoYuklenenId === personId);
  useEffect(() => {
    if (!personId || !fotoVar) return undefined;
    let iptal = false;
    let url = null;
    async function oku() {
      try {
        url = await fetchKisiFotograf(personId);
        if (iptal) {
          URL.revokeObjectURL(url);
          url = null;
          return;
        }
        setFoto({ id: personId, url });
      } catch {
        if (!iptal) setFoto(null); // okunamayan vesikalık yer tutucuda kalır
      }
    }
    oku();
    return () => {
      iptal = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [personId, fotoVar, fotoSayac]);
  const fotoUrl = fotoVar && foto && foto.id === personId ? foto.url : null;

  async function fotografSecildi(e) {
    const dosya = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!dosya) return;
    if (!FOTO_TURLERI.test(dosya.type) && !FOTO_UZANTILARI.test(dosya.name)) {
      onError?.(t("kunyeBasligi.fotoGecersizTip"));
      return;
    }
    setYukleniyor(true);
    try {
      await uploadKisiFotograf(personId, dosya);
      setFotoYuklenenId(personId);
      setFotoSayac((n) => n + 1);
      onChanged?.(t("kunyeBasligi.fotoGuncellendi"));
    } catch (err) {
      onError?.(err.status == null ? t("kunyeBasligi.sunucuYanitVermedi") : err.message);
    } finally {
      setYukleniyor(false);
    }
  }

  function kaydedildi(mesaj) {
    setYenile((n) => n + 1);
    onChanged?.(mesaj);
  }

  // Künye okunduysa o, değilse kişi satırı. Okunuyorsa satırdaki değer gösterilir (titreme olmasın).
  const ad = k ? k.ad : person?.name;
  const soyad = k ? k.soyad : person?.surname;
  const adSoyad = [ad, soyad].filter(Boolean).join(" ");
  const kimlikNo = k?.kimlikNo || person?.tcNo || person?.tcNoMasked || null;
  const telefon = k ? k.telefon : person?.phone;
  const eposta = k ? k.eposta : person?.email;
  const uyruk = k ? (k.uyrukAdi || (UYRUK_ANAHTAR[k.uyrukId] && t(`kunyeBasligi.${UYRUK_ANAHTAR[k.uyrukId]}`)) || null) : null;
  // #479: kartın basılacağı firma (Kart Yazdır şeridiyle aynı seçim) + diğerleri. Fuar seçili değilse ya da görevlendirme yoksa null.
  const firmaKarari = fuarId && firmalar?.length ? kartKararBirlestir(firmalar, { tercihFirmaId }) : null;
  const firmaAdlari = firmaKarari ? firmaKarari.digerFirmalar.map(firmaAdi).join(", ") : "";
  const ulkeAdi = ulkeId != null && ulkeAdlari ? ulkeAdlari[String(ulkeId)] : null;
  const birlesik = Boolean(k?.birlestirildigiPersonId);
  const duzenlenebilir = !salt && !birlesik;
  const bekle = <span className="text-muted fw-normal">…</span>;

  return (
    <Card className="mb-3">
      <CardBody>
        <Row className="align-items-start g-3">
          <Col xs="auto">
            <div className="position-relative flex-shrink-0" style={{ width: 88, height: 88 }}>
              <div className="rounded-3 border d-flex align-items-center justify-content-center bg-light overflow-hidden w-100 h-100">
                {fotoUrl
                  ? <img src={fotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <User size={40} className="text-muted" />}
              </div>
              {!salt && (
                <>
                  <input ref={girdiRef} type="file" accept="image/jpeg,image/png,image/webp" className="d-none" onChange={fotografSecildi} />
                  <Button
                    color="light" size="sm" disabled={yukleniyor} title={t("kunyeBasligi.fotografiDegistir")} aria-label={t("kunyeBasligi.fotografiDegistir")}
                    className="position-absolute rounded-circle border shadow-sm p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 26, height: 26, right: -6, bottom: -6 }}
                    onClick={() => girdiRef.current?.click()}
                  >
                    {yukleniyor ? <Spinner size="sm" style={{ width: 12, height: 12 }} /> : <Camera size={13} />}
                  </Button>
                </>
              )}
            </div>
          </Col>

          <Col>
            <h3 className="fw-bold mb-1 lh-sm">{adSoyad || "—"}</h3>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="d-inline-flex align-items-center gap-1 fs-5 fw-semibold font-monospace" title={t("kunyeBasligi.tcPasaportNo")}>
                <IdCard size={16} className="text-muted" />{kimlikNo || "—"}
              </span>
              <span className="text-muted small">#{personId}</span>
            </div>
            <Row className="g-3 mt-0">
              {firmaKarari && (
                <Col xs={6} sm="auto">
                  <Bilgi Icon={Building2} etiket={t("kunyeBasligi.firma")}>
                    <span>{firmaAdi(firmaKarari.secili)}</span>
                    {firmaKarari.digerFirmaSayisi > 0 && (
                      <span
                        className="text-muted fw-normal ms-1" style={{ cursor: "help" }}
                        title={t("kunyeBasligi.digerFirmalar", { adlar: firmaAdlari })}
                      >
                        +{firmaKarari.digerFirmaSayisi}
                      </span>
                    )}
                  </Bilgi>
                </Col>
              )}
              <Col xs={6} sm="auto">
                <Bilgi Icon={Globe} etiket={t("kunyeBasligi.uyruk")}>{yukleniyorKunye ? bekle : uyruk}</Bilgi>
              </Col>
              <Col xs={6} sm="auto">
                <Bilgi Icon={Phone} etiket={t("kunyeBasligi.telefon")}>{telefon}</Bilgi>
              </Col>
              <Col xs={6} sm="auto">
                <Bilgi Icon={Mail} etiket={t("kunyeBasligi.ePosta")}>{eposta}</Bilgi>
              </Col>
              {k?.pasaportNo && (
                <Col xs={6} sm="auto">
                  <Bilgi Icon={BookOpen} etiket={t("kunyeBasligi.pasaportNo")}>
                    <span className="font-monospace">{k.pasaportNo}</span>{ulkeAdi && <span> · {ulkeAdi}</span>}
                  </Bilgi>
                </Col>
              )}
            </Row>
          </Col>

          {duzenlenebilir && (
            <Col xs={12} md="auto" className="text-end">
              {/* #647: SMS ile geçici kişi paneline davet (backend #644) */}
              <PanelDavetiDugmesi personId={personId} adSoyad={adSoyad} className="me-2" />
              <Button
                color="primary" outline size="sm" disabled={!k} onClick={() => setDuzenleAcik(true)}
                title={kunyeHata ? t("kunyeBasligi.kunyeOkunamadi", { hata: kunyeHata }) : undefined}
              >
                <Pencil size={14} className="me-1" />{t("kunyeBasligi.duzenleVeGor")}
              </Button>
            </Col>
          )}
        </Row>
      </CardBody>

      {duzenleAcik && k && (
        <KunyeDuzenleModal
          personId={personId}
          fuarId={fuarId}
          kunye={k}
          bilgiler={person?.bilgiler}
          onClose={() => setDuzenleAcik(false)}
          onSaved={kaydedildi}
        />
      )}
    </Card>
  );
}
